import React, {useEffect, useRef, useState} from "react";
import './signIn.scss';
import {Card} from "primereact/card";
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import ForgotPasswordForm from "./ForgotPasswordForm";
import {LoginFormRef, SignInForm} from "./SignInForm";
import * as StorageConstants from '../../Models/SecureStorageConstants'
import ResetPasswordForm from "./ResetPasswordForm";
import DashlyIcon from '../../Images/blacktextnewLogo.png'

import {Message} from "primereact/message";
import {reloadBrowserBrandingFromOrigin} from "../../Template/custom-styling/applyBrowserBranding";
import {useUrlSpecificStyling} from "../../Template/custom-styling/urlSpecificStyling";
import {ISystemBackendVersion, SystemService} from "../../Services/SystemService";
import {toast} from "react-toastify";

const SignIn = () => {
    const {t} = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

	const [frontendVersion, setFrontendVersion] = useState<ISystemBackendVersion | undefined>();

    const [showLoginPage, setShowLoginPage] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const resetToken = location.pathname.split("/").pop(); //TODO replace??
    const [showResetPassword, setShowResetPassword] = useState(location.pathname.includes("/reset-password"));

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const signInRef = useRef<LoginFormRef>(null);

	// Customer CSS for this origin: applied before paint from the value the AppLoader
	// already resolved (or the cache), refreshed when the fetch completes, removed on unmount.
	useUrlSpecificStyling();

	useEffect(() => {
		loadVersion();
		void reloadBrowserBrandingFromOrigin();
	}, []);

	const loadVersion = () => {
		new SystemService().retrieveFrontendVersion().then((response) => {
			setFrontendVersion(response);
		}).catch((error) => {
			toast.error(t('common.error'), {autoClose: 1500})
		})
	}

    useEffect(() => {
        setShowLoginPage(true);
        if (secureLocalStorage.getItem(StorageConstants.USER) && secureLocalStorage.getItem(StorageConstants.ACCESS_TOKEN)) {
            //setShowLoginPage(false);
            navigate('/', {replace: true});
        } 

        if (location.hash === "#logout") {
            setSuccessMessage(t("login.message.logout"));
            const storedAvatars = JSON.parse(localStorage.getItem("userAvatars") || "{}");
            Object.keys(storedAvatars).forEach(key => {
                delete storedAvatars[key];
            });
            localStorage.setItem("userAvatars", JSON.stringify(storedAvatars));
            window.history.replaceState(null, "", window.location.pathname);
        }
    }, [navigate, location]);

    // Clear errorMessage if successMessage is set
    useEffect(() => {
        if (successMessage) {
            setErrorMessage(null);
        }
    }, [successMessage]);

    // Clear successMessage if errorMessage is set
    useEffect(() => {
        if (errorMessage) {
            setSuccessMessage(null);
        }
    }, [errorMessage]);

    const toggleForgotPassword = () => {
        setShowForgotPassword(!showForgotPassword);
    };
    const toggleResetPassword = () => {
        setShowResetPassword(!showResetPassword);
    };

    const onBackToLoginAction = (username?: string, password?: string) => {
        if (showResetPassword && username && password) {
            signInRef.current?.signInAction(username, password);
        }
        if (showResetPassword) toggleResetPassword();
    }


    if (!showLoginPage) return null;
    return (
        <div className="login_main">
            <Card className="card">
                <div className="header">
                    <img src={DashlyIcon} className="dashlyicon" alt="Dashly" />
                </div>

                {successMessage && (
                    <Message severity="success" text={successMessage} className="sign-in__message w-full" />
                )}
                {errorMessage && (
                    <Message severity="error" text={errorMessage} className="sign-in__message w-full" />
                )}

                <div className={`form-container ${showResetPassword || showForgotPassword ? 'hidden' : ''}`}>
                    <SignInForm ref={signInRef}
                                setSuccessMessage={setSuccessMessage}
                                setErrorMessage={setErrorMessage}
                                toggleForgotPassword={toggleForgotPassword}
                                toggleResetPassword={toggleResetPassword}/>
                </div>

                <div className={`form-container ${showResetPassword ? '' : 'hidden'}`}>
                    <ResetPasswordForm setSuccessMessage={setSuccessMessage}
                                       setErrorMessage={setErrorMessage}
                                       uuid={resetToken}
                                       onBackToLogin={onBackToLoginAction}/>
                </div>

                <div className={`form-container ${showForgotPassword ? '' : 'hidden'}`}>
                    <ForgotPasswordForm setSuccessMessage={setSuccessMessage}
                                        setErrorMessage={setErrorMessage}
                                        onBackToLogin={toggleForgotPassword}/>
                </div>
            </Card>

	        {frontendVersion && (
				<div className="settings-version-block">
			        <span>{frontendVersion.projectName}</span>
			        <span>{frontendVersion.projectBuildVersion}</span>
			        <span>{frontendVersion.projectBuildTimestamp}</span>
		        </div>
	        )}
        </div>
    );
};

export default SignIn;
